import { useState } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { 
  AppBar, 
  Box, 
  Drawer, 
  IconButton, 
  Toolbar, 
  Typography, 
  List, 
  ListItem, 
  ListItemIcon, 
  ListItemText,
  Divider,
  ListItemButton,
 // Avatar,
  Menu,
  MenuItem,
  Container
} from '@mui/material';
import { 
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  Business as BusinessIcon,
  Subscriptions as SubscriptionsIcon,
  VpnKey as LicenseIcon,
  Inventory as ProductsIcon,
  AccountCircle,
 // ChevronLeft as ChevronLeftIcon
} from '@mui/icons-material';
import { useAuth } from '@/context/AuthContext';

const drawerWidth = 240;

export default function MainLayout() {
  const [drawerOpen, setDrawerOpen] = useState(true);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleDrawerToggle = () => {
    setDrawerOpen(!drawerOpen);
  };

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleProfile = () => {
    handleClose();
    navigate('/profile');
  };

  const handleLogout = async () => {
    handleClose();
    await logout();
    navigate('/login');
  };

  // Navigation items based on user role
  const getNavigationItems = () => {
    const baseItems = [
      { text: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard' },
      { text: 'Subscriptions', icon: <SubscriptionsIcon />, path: '/subscriptions' },
      { text: 'Licenses', icon: <LicenseIcon />, path: '/licenses' },
    ];

    // Add role-specific items
    if (user?.role === 'distributor') {
      return [
        ...baseItems,
        { text: 'Resellers', icon: <BusinessIcon />, path: '/resellers' },
        { text: 'Customers', icon: <PeopleIcon />, path: '/customers' },
        { text: 'Products', icon: <ProductsIcon />, path: '/products' },
      ];
    } else if (user?.role === 'reseller') {
      return [
        ...baseItems,
        { text: 'Customers', icon: <PeopleIcon />, path: '/customers' },
        { text: 'Products', icon: <ProductsIcon />, path: '/products' },
      ];
    }

    return baseItems;
  };

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar 
        position="fixed" 
        sx={{ 
          zIndex: (theme) => theme.zIndex.drawer + 1,
          transition: (theme) => theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
          ...(drawerOpen && {
            marginLeft: drawerWidth,
            width: `calc(100% - ${drawerWidth}px)`,
            transition: (theme) => theme.transitions.create(['width', 'margin'], {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen,
            }),
          }),
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            onClick={handleDrawerToggle}
            edge="start"
            sx={{ mr: 2 }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            License Management System
          </Typography>
          {user && (
            <div>
              <IconButton
                size="large"
                aria-label="account of current user"
                aria-controls="menu-appbar"
                aria-haspopup="true"
                onClick={handleMenu}
                color="inherit"
              >
                <AccountCircle />
              </IconButton>
              <Menu
                id="menu-appbar"
                anchorEl={anchorEl}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
                keepMounted
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                open={Boolean(anchorEl)}
                onClose={handleClose}
              >
                <MenuItem disabled>
                  <Typography variant="body2">{user.email}</Typography>
                </MenuItem>
                <MenuItem disabled>
                  <Typography variant="body2" color="textSecondary">
                    Role: {user.role.charAt(0).toUpperCase() + user.role.slice(1)}
                  </Typography>
                </MenuItem>
                <Divider />
                <MenuItem onClick={handleProfile}>Profile</MenuItem>
                <MenuItem onClick={handleLogout}>Logout</MenuItem>
              </Menu>
            </div>
          )}
        </Toolbar>
      </AppBar>
      <Drawer
        variant="permanent"
        open={drawerOpen}
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          [`& .MuiDrawer-paper`]: { 
            width: drawerWidth, 
            boxSizing: 'border-box',
            ...(drawerOpen ? {
              overflowX: 'hidden',
            } : {
              overflowX: 'hidden',
              width: '64px',
            }),
            transition: (theme) => theme.transitions.create('width', {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen,
            }),
          },
        }}
      >
        <Toolbar />
        <Box sx={{ overflow: 'auto' }}>
  <List>
    {getNavigationItems().map((item) => (
      <ListItem 
        key={item.text}
        disablePadding // Add this to remove default padding
      >
        <ListItemButton
          onClick={() => navigate(item.path)}
          sx={{
            py: 1,
            minHeight: 48,
            justifyContent: drawerOpen ? 'initial' : 'center',
          }}
        >
          <ListItemIcon sx={{
            minWidth: 0,
            mr: drawerOpen ? 2 : 'auto',
            justifyContent: 'center',
          }}>
            {item.icon}
          </ListItemIcon>
          {drawerOpen && <ListItemText primary={item.text} />}
        </ListItemButton>
      </ListItem>
    ))}
  </List>
</Box>
      </Drawer>
      <Box component="main" sx={{ flexGrow: 1, p: 3 }}>
        <Toolbar />
        <Container maxWidth="xl">
          <Outlet />
        </Container>
      </Box>
    </Box>
  );
}
